import { type ReactNode } from 'react';
import type { Creator } from '@/types';
import { useAuth } from '@/hooks/use-auth';
import { formatRelativeDate } from '@/utils/relative-date';
import {
  formatFollowers,
  rankToFollowerRange,
} from '../utils/creator-formatters';
import { CreatorStatusBadge } from './creator-status-badge';
import { CreatorCategoryBadge } from './creator-category-badge';
import { CollectorCell } from './collector-cell';
import { PostLink, UserCell } from './creator-table-cells';

const TD = 'px-md py-sm';
const NUM = `${TD} whitespace-nowrap text-xs tabular-nums`;
const TR =
  'cursor-pointer border-b border-white/[0.03] transition-colors duration-150 hover:bg-white/[0.05]';

function DataCells({ creator }: { creator: Creator }): ReactNode {
  return (
    <>
      <td className={`${NUM} text-text-secondary`}>
        {formatFollowers(creator.followers)}
      </td>
      <td className={TD}>
        <CreatorCategoryBadge
          type={creator.creator_type}
          category={creator.category}
        />
      </td>
      <td className={`${NUM} text-text-tertiary`}>
        {rankToFollowerRange(creator.rank)}
      </td>
      <td className={TD}>
        <CreatorStatusBadge status={creator.status} />
      </td>
      <td className={`${NUM} text-[11px] text-text-tertiary`}>
        {formatRelativeDate(creator.created)}
      </td>
    </>
  );
}

type RowProps = { creator: Creator; onClick: () => void };

export function CreatorTableRow({ creator, onClick }: RowProps): ReactNode {
  const { isAdmin } = useAuth();
  const dim = creator.is_void || !!creator.deleted_at;
  const rowCls = dim ? `${TR} opacity-60` : TR;
  return (
    <tr className={rowCls} onClick={onClick}>
      <td className={TD}>
        <UserCell creator={creator} />
      </td>
      <DataCells creator={creator} />
      {isAdmin && (
        <td className={TD}>
          <CollectorCell creator={creator} />
        </td>
      )}
      <td className={`${TD} text-center`}>
        <PostLink url={creator.post_url} />
      </td>
    </tr>
  );
}
